import { ReactNode, useEffect, useMemo, useState } from "react";
import { Typography as T } from "@mui/material";
import { Box } from "@mui/system";

import { toDateOnlyLocal } from "helpers/date";
import { reduceIconPath } from "helpers/functions";
import { palette } from "theme/palette";

type Props = {
  data: {
    date: Date;
    time: string;
    label: ReactNode;
    dateTitle?: string;
    timeTitle?: string;
  };
};

// "HH:mm" -> "h:mm AM/PM"
function formatHMTo12h(hm?: string): string {
  if (!hm) return "";
  const match = /^(\d{1,2}):(\d{2})$/.exec(hm);
  if (!match) return hm; // fallback if unexpected shape
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  if (Number.isNaN(hours)) return hm;

  const suffix = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // 0 -> 12 AM, 12 -> 12 PM
  return `${hours}:${minutes} ${suffix}`;
}

export const SummaryDateItem = ({
  data: { date, time, label, dateTitle = "Date:", timeTitle = "Time:" },
}: Props) => {
  // styles
  const [formattedDate, setFormattedDate] = useState("");
  const [formattedTime, setFormattedTime] = useState("");

  // Normalize display date to a safe local date for rendering text
  const safeDisplayDate = useMemo(() => toDateOnlyLocal(date) ?? date, [date]);

  useEffect(() => {
    setFormattedDate(
      safeDisplayDate.toLocaleString("en", { day: "numeric" }) +
        " " +
        safeDisplayDate.toLocaleString("en", { month: "long" }) +
        ", " +
        safeDisplayDate.toLocaleString("en", { year: "numeric" })
    );

    setFormattedTime(time ? formatHMTo12h(time) : "12:00 AM");
  }, [safeDisplayDate, time]);

  return (
    <Box
      sx={{
        padding: "21px 17px 25px 17px",
        border: `1px solid ${palette.grey}`,
        borderRadius: "10px",
        width: "100%",
      }}
    >
      <T
        variant="cardFooter"
        sx={{
          marginLeft: "8px",
        }}
      >
        {label}
      </T>
      <Box
        sx={{
          marginTop: "11px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          alignContent: "center",
        }}
      >
        <img src={reduceIconPath("Calendar.svg")} alt="calendar icon" />
        <Box
          sx={{
            marginLeft: "8px",
            display: "flex",
            justifyContent: "flex-start",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <T variant="cardLabelText">{dateTitle}</T>
            <T
              variant="secondaryText"
              sx={{
                marginLeft: "4px",
                marginTop: "2px",
              }}
            >
              {formattedDate}
            </T>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <T variant="cardLabelText">{timeTitle}</T>
            <T
              variant="secondaryText"
              sx={{
                marginLeft: "4px",
                marginTop: "2px",
              }}
            >
              {formattedTime}
            </T>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
